import {
  FooterContainer,
  StepTitle,
  WizardContentWrapper,
  FooterSkipButton,
  StyledStepTitleWrapper,
} from "@/components/onboarding/components/welcome-wizard/welcome-wizard.styles";
import type { StepProps } from "@/components/onboarding/components/welcome-wizard/welcome-wizard.types";
import {
  MDSIconButton,
  MDSText,
  MDSTextVariant,
  MDSTextWeight,
  MDSIconKind,
  MDSButton,
  MDSButtonVariant,
} from "@/mds";
import { StepGroups } from "@/components/onboarding/components/welcome-wizard/welcome-wizard.config";
import {
  StepContentWithTabs,
  StyledStepContentWithTabsContainer,
} from "@/components/onboarding/components/welcome-wizard/tabs";
import { useAppStore } from "@/store";
import { observer } from "mobx-react-lite";
import styled from "@emotion/styled";

export const AppleNotesOneClickStepDesktop: React.FC<StepProps> = observer(
  ({ context, handleTabSelect, onSkip, onClose }) => {
    const { store } = useAppStore();

    const handleContinue = async () => {
      const wizardType = context.wizardType || null;

      // For onboarding: set flags to reopen wizard after import completes
      if (wizardType === "welcome") {
        store.oneClickImport.shouldReopenWizardAfterImport = true;
        store.oneClickImport.wizardStepToReopenOn = "download-apps"; // Success: go to next step
        store.oneClickImport.wizardStepToFallbackTo = "apple-notes-1"; // Failure: stay on current step
      }

      // Close the wizard modal (unmount)
      onClose?.();

      // Call startImport which will open the one-click-import-modal
      // Parent will watch for modal close and reopen wizard if flags are set
      await store.oneClickImport.startImport();
    };

    const isWelcomeWizard = context.wizardType === "welcome";

    const stepContent = (
      <>
        <MDSText variant={MDSTextVariant.ParagraphSm} weight={MDSTextWeight.Regular}>
          A system dialog will open with your Apple Notes folder selected. Click &apos;Open&apos; to
          grant Mem access.
        </MDSText>
        {!isWelcomeWizard && (
          <StyledButtonWrapper>
            <MDSButton variant={MDSButtonVariant.Default} onClick={handleContinue}>
              Continue
            </MDSButton>
          </StyledButtonWrapper>
        )}
      </>
    );

    return (
      <WizardContentWrapper>
        <StyledStepTitleWrapper>
          <StepTitle>
            {isWelcomeWizard ? "Select notes from Apple Notes" : "Import Notes"}
          </StepTitle>
          {context.wizardType === "import" && (
            <MDSIconButton iconKind={MDSIconKind.Exit} onClick={onClose} />
          )}
        </StyledStepTitleWrapper>
        {isWelcomeWizard ? (
          <StyledStepContentWithTabsContainer>{stepContent}</StyledStepContentWithTabsContainer>
        ) : (
          <StepContentWithTabs
            selectedAppId={context?.selectedAppId || null}
            onAppSelect={handleTabSelect}
            tabConfig={StepGroups["IMPORT"].tabConfig}
          >
            {stepContent}
          </StepContentWithTabs>
        )}
        {(onSkip || isWelcomeWizard) && (
          <FooterContainer align="space-between">
            {onSkip && (
              <FooterSkipButton onClick={onSkip} isDisabled={!onSkip}>
                Skip for now
              </FooterSkipButton>
            )}
            {isWelcomeWizard && (
              <MDSButton variant={MDSButtonVariant.Default} onClick={handleContinue}>
                Continue
              </MDSButton>
            )}
          </FooterContainer>
        )}
      </WizardContentWrapper>
    );
  }
);

const StyledButtonWrapper = styled.div`
  display: flex;
  align-self: flex-start;
`;
